import { useMemo } from "react";
import { motion } from "framer-motion";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
} from "recharts";

export const PositionPriceChart = ({ chartData }) => {
  const { bars, lastPrice, ticker } = chartData || { bars: [], lastPrice: 0, ticker: null };

  const data = useMemo(
    () =>
      bars.flatMap((b) => [
        { name: `${b.ticker} avg`, value: b.avgBuy, type: "avg" },
        { name: `${b.ticker} now`, value: b.current, type: "current" },
      ]),
    [bars],
  );

  if (!bars.length) {
    return (
      <motion.div
        className="panel lg:col-span-7 lg:row-span-2 flex flex-col min-h-[280px] p-4"
        data-testid="position-price-chart"
      >
        <span className="label-uppercase text-magenta">Position prices</span>
        <p className="mt-2 text-sm text-text-secondary font-mono">No active positions to chart.</p>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, delay: 0.2 }}
      className="panel lg:col-span-7 lg:row-span-2 flex flex-col min-h-[280px]"
      data-testid="position-price-chart"
    >
      <div className="panel-header">
        <h3 className="font-display text-sm font-bold tracking-tight uppercase">
          Position Price Levels
        </h3>
        <span className="label-micro num">
          {ticker || "ALL"} · Rp {Number(lastPrice).toLocaleString("id-ID")}
        </span>
      </div>
      <div className="flex-1 min-h-[200px] p-2">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--chart-grid))" />
            <XAxis dataKey="name" tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }} />
            <YAxis tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }} />
            <Tooltip
              contentStyle={{
                background: "hsl(var(--surface))",
                border: "1px solid hsl(var(--border))",
                fontFamily: "JetBrains Mono",
                fontSize: 11,
              }}
            />
            <ReferenceLine y={lastPrice} stroke="hsl(var(--accent-highlight))" strokeDasharray="4 4" />
            <Bar dataKey="value" radius={[1, 1, 0, 0]}>
              {data.map((entry, i) => (
                <Cell
                  key={i}
                  fill={
                    entry.type === "current"
                      ? "hsl(var(--accent-positive))"
                      : "hsl(var(--muted-foreground))"
                  }
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </motion.div>
  );
};

export default PositionPriceChart;
